import {useContext, useEffect, useRef, useState} from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { closeWebsocket, disposeWebsocket, startWebsocket } from '../../../utils/net';
import { ChatBoard } from '../../Board/ChatBoard';
import { DetailBoard } from '../../Board/DetailBoard';
import { GenerateBoard } from '../../Board/GenerateBoard';
import { Image3dProdBoard } from '../../Board/Image3dProdBoard';
import style from './modal.module.css';
import {
    assistantChatStatusAtom,
    canStartWsAtom,
    chatGuessAtom,
    chatHistoryAtom,
    chatLangAtom,
    guessChatStatusAtom,
    meshProfileAtom,
    modalObjAtom,
    needStartWsAtom,
    promptAtom,
    stopChatAtom,
    taskDetailAtom,
    taskInitAtom,
    showWalletAtom,
    lastGenerateInfoAtom,
    optionOpenAtom,
    currentNavAtom,
    checkImgConfirmAtom,
    checkDownloadAtom,
    imageWorkflowsAtom,
    selectedImageIndexAtom,
    optionModeCloseAtom,
    optionModeRestAtom,
    optionSharpAtom
    // setGenerateAtom,
} from '../../../store';
import {checkMobile, exportToImage} from '../../../utils/format';
import { isNotEmpty } from '../../../utils/format';
import { ModalClickContext } from '../../../utils/context';
import { Wallet } from '../Wallet'
//import { Recommend } from '../Recommend';
import RodinPreviewBoard from '../../Board/RodinPreviewBoard';
import RodinGenerateBoard from '../../Board/RodinGenerateBoard';
import * as Sentry from "@sentry/react";
import {ErrorPage} from "../../ErrorPage";
import {FiChevronsDown, FiChevronsUp} from "react-icons/fi";

function Modal() {
    const handleModalClick = useContext(ModalClickContext);
    const showWallet = useRecoilValue(showWalletAtom);
    const lastGenerateInfo = useRecoilValue(lastGenerateInfoAtom);
    const [checkDownload,setCheckDownload] = useRecoilState(checkDownloadAtom);
    const taskInit = useRecoilValue(taskInitAtom);
    const [chatHistory, setChatHistory] = useRecoilState(chatHistoryAtom);
    const setCanStartWs = useSetRecoilState(canStartWsAtom);
    const [chatGuess, setChatGuess] = useRecoilState(chatGuessAtom);
    const [checkImgConfirm,setCheckImgConfirm] = useRecoilState(checkImgConfirmAtom);
    const [imageWorkflows, setImageWorkflows] = useRecoilState(imageWorkflowsAtom);
    const setMeshProfile = useSetRecoilState(meshProfileAtom);
    const setAssistantChatStatus = useSetRecoilState(assistantChatStatusAtom);
    const setGuessChatStatus = useSetRecoilState(guessChatStatusAtom);
    const [currentNav, setCurrentNav] = useRecoilState(currentNavAtom);
    const [selectedImageIndex, setSelectedImageIndex] = useRecoilState(selectedImageIndexAtom);
    const [optionModeRest, setOptionModeRest] = useRecoilState(optionModeRestAtom)
    const [optionSharp, setOptionSharp] = useRecoilState(optionSharpAtom)
    const [prompt, setPrompt] = useRecoilState(promptAtom);
    const [stopChat, setStopChat] = useRecoilState(stopChatAtom);
    const chatLang = useRecoilValue(chatLangAtom);
    const [needStartWs, setNeedStartWs] = useRecoilState(needStartWsAtom);
    const isListenRef = useRef(false);
    const chatHistoryRef = useRef({});
    const chatGuessRef = useRef('');
    const promptRef = useRef('');
    const taskDetail = useRecoilValue(taskDetailAtom);
    const modalObj = useRecoilValue(modalObjAtom);
    const [, setOptionModeClose] = useRecoilState(optionModeCloseAtom)
    const dialogRef = useRef(null);
    const Fallback = <ErrorPage />;
    const [isAtTop, setIsAtTop] = useState(true);
    const [,setOptionOpen]=useRecoilState(optionOpenAtom)

    useEffect(() => {
        chatHistoryRef.current = { ...chatHistory };
    }, [chatHistory]);

    useEffect(() => {
        promptRef.current = prompt;
    }, [prompt]);

    useEffect(() => {
        if (stopChat) {
            closeWebsocket();
        }
    }, [stopChat]);

    useEffect(() => {
        if (!needStartWs) return;
        setChatGuess([]);
        setPrompt('');
        setChatHistory({});
        setAssistantChatStatus('');
        setStopChat(false);
        isListenRef.current = false;
        chatHistoryRef.current = {};
        chatGuessRef.current = '';
        promptRef.current = '';

    }, [needStartWs]);

    useEffect(() => {
        if (!taskInit) {
            closeWebsocket();
            disposeWebsocket();
            return;
        }
        (async () => {
            if (isListenRef.current) return;
            isListenRef.current = true;
            const ws = await startWebsocket(taskInit.subscription, taskInit.task_uuid, chatLang);
            setCanStartWs(true);
            bindWsListeners(ws);
        })();

    }, [taskInit]);

    useEffect(() => {
        if (!taskDetail) return;
        if (isNotEmpty(taskDetail.chat_history)) {
            setChatHistory(
                taskDetail.chat_history.reduce(
                    (res, cur) => ({
                        ...res,
                        [cur.chat_uuid]: { ...cur, timeStamp: new Date(cur.time).getTime() },
                    }),
                    {},
                ),
            );
        } else {
            setChatHistory({});
        }
        setMeshProfile({
            model_preview_info: taskDetail.model_preview_info,
            task_uuid: taskDetail.task_uuid,
            time: Date.now(),
        });

    }, [taskDetail]);

    const exportDialog = async () => {
        await exportToImage(dialogRef.current, 'dialog');
    };

    const bindWsListeners = (ws) => {
        setNeedStartWs(false);
        ws.off('assistant');
        ws.off('guess');
        ws.off('summary');
        ws.on('assistant', (ev) => {
            const currentChat = { ...(chatHistoryRef.current[ev.chat_uuid] || {}) };
            setAssistantChatStatus(ev.content);
            if (ev.content === '[START]') {
                currentChat.chat_uuid = ev.chat_uuid;
                currentChat.provider = ev.provider;
                currentChat.timeStamp = Date.now();
                currentChat.content = '';
            } else if (ev.content !== '[END]') {
                currentChat.content += ev.content;
            }
            setChatHistory({
                ...chatHistoryRef.current,
                [ev.chat_uuid]: currentChat,
            });
        })
        function isValidContent(content) {
            if (content.substring(0, 3) !== '1. ') {
                return false;
            }

            const parts = content.split(/(\d+\.) /);
            for (let i = 1; i < parts.length; i += 2) {
                if (i > 1 && !(parts[i - 1].endsWith('[SEP]') || parts[i - 1].endsWith('\n'))) {
                    setChatGuess(chatGuess.slice(-2))
                    return false;
                }
            }

            const endSubstrings = [content.slice(-1), content.slice(-2), content.slice(-3), content.slice(-4), content.slice(-5)];
            const invalidEndSubstrings = ['[', '[S', '[SE', '[SEP', '[SEP]'];
            if (endSubstrings.some(substr => invalidEndSubstrings.includes(substr))) {
                return false;
            }

            return true;
        }

        ws.on('guess', (ev) => {
            setGuessChatStatus(ev.content);
            if (ev.content === '[START]') {
                chatGuessRef.current = '';
            } else if (ev.content !== '[END]') {
                chatGuessRef.current += ev.content;
                if (!isValidContent(chatGuessRef.current)) {
                    return;
                }

                setChatGuess(chatGuessRef.current.split(/\n|\[SEP\]/).map(s => s.trim()));
            }
        });

        ws.on('summary', (ev) => {
            if (ev.content === '[START]') {
                promptRef.current = '';
            } else if (ev.content !== '[END]') {
                promptRef.current += ev.content;
                setPrompt(promptRef.current);
            }
        });
    };

    const handleOutsideClick = () => {
        handleModalClick();
        setCheckImgConfirm(false)
        setCheckDownload(false)
        setOptionOpen(false)
        setImageWorkflows([])
        setSelectedImageIndex(0)
        setOptionModeClose(false)
        setOptionModeRest(false)
        setOptionSharp(false)

        if (modalObj.type === 'generate') {
            return;
        }

        const url = new URL(window.location.href)

        if (modalObj.type === 'DreamFace') {
            url.pathname = '/chatavatar'
        } else if (modalObj.type === 'ImagineFace') {
            url.pathname = '/imageto3d'
        } else if (modalObj.type === 'Rodin') {
            if (currentNav === 'Rodin') {
                window.history.pushState(null, '', '/proxy/rodin'); // For netease only
            } else if (currentNav === 'OmniCraft') {
                window.history.pushState(null, '', '/omnicraft');
            }
        } else {
            url.pathname = '/'
        }

        window.history.pushState(null, '', url)
    }

    window.exportDialog = exportDialog;
    const isMobile = checkMobile()

    let touchStartX = 0;
    let touchStartY = 0;
    let scrollDirectionLocked = false;

    const handleTouchStart = (event) => {
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        scrollDirectionLocked = false;
    };

    const handleTouchMove = (event) => {
        if (!dialogRef.current || scrollDirectionLocked) return;

        const touchEndX = event.touches[0].clientX;
        const touchEndY = event.touches[0].clientY;
        const diffX = Math.abs(touchEndX - touchStartX);
        const diffY = Math.abs(touchEndY - touchStartY);

        if (!scrollDirectionLocked) {
            if (diffX * 2 > diffY) {
                dialogRef.current.style.overflowY = 'hidden';
            } else {
                dialogRef.current.style.overflowY = 'auto';
            }
            scrollDirectionLocked = true;
        }
    };

    const handleTouchEnd = () => {
        if (dialogRef.current) {
            dialogRef.current.style.overflowY = 'auto';
        }
    };

    const checkScrollPosition = () => {
        if (!dialogRef.current) return;
        const { scrollTop } = dialogRef.current;
        setIsAtTop(scrollTop === 0);
    };

    useEffect(() => {
        if (!isMobile) return;
        const currentDialog = dialogRef.current;
        if (currentDialog) {
            currentDialog.addEventListener('scroll', checkScrollPosition);
        }
        return () => {
            if (currentDialog) {
                currentDialog.removeEventListener('scroll', checkScrollPosition);
            }
        };
    }, []);

    const scrollToTop = () => {
        dialogRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToBottom = () => {
        dialogRef.current.scrollTo({ top: dialogRef.current.scrollHeight, behavior: 'smooth' });
    };

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    return (
        <div>
            <div className={`${style.mask} ${modalObj.type === 'generate' ? style.lowZIndex : ""}`}
                 onPointerDown={handleOutsideClick}/>
            <div
                className={`${isMobile ? style.cardBoxMobile : style.cardBox} ${modalObj.type === 'Rodin' ? style.rodinWrapper : ""}`}
                onPointerDown={(ev) => {
                    ev.stopPropagation();
                    const optionDev=document.getElementById('rodinOption')
                    const optionClick=document.getElementById('optionClick')
                    if((optionDev && optionDev.contains(ev.target)) || (optionClick && optionClick.contains(ev.target))){
                        return
                    }else{
                        setOptionOpen(false)
                    }
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                ref={dialogRef}>
                {modalObj.type === "generate" && showWallet.open && !lastGenerateInfo.privateType && (
                    <div className={style.groupWallet}>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <Wallet type='textWallet'/>
                        </Sentry.ErrorBoundary>
                        <div className={`${style.walletMask} ${showWallet.open ? style.show : style.hide}`}></div>
                    </div>
                )}
                {modalObj.type === 'generate' && (
                    <>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <GenerateBoard width="52%"/>
                        </Sentry.ErrorBoundary>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <ChatBoard width="48%"/>
                        </Sentry.ErrorBoundary>
                    </>
                )}
                {modalObj.type === 'DreamFace' && (
                    <>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <DetailBoard width="52%" type="textto3d"/>
                        </Sentry.ErrorBoundary>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <ChatBoard width="48%"/>
                        </Sentry.ErrorBoundary>
                    </>
                )}
                {modalObj.type === 'ImagineFace' && (
                    <>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <DetailBoard width="55%" type="imageto3d"/>
                        </Sentry.ErrorBoundary>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <Image3dProdBoard width="45%"/>
                        </Sentry.ErrorBoundary>
                    </>
                )}
                {modalObj.type === 'Rodin' && (
                    <>
                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <RodinPreviewBoard/>
                        </Sentry.ErrorBoundary>

                        <Sentry.ErrorBoundary
                            fallback={({ error, resetError }) => (
                                <ErrorPage
                                    errorMessage={error?.message}
                                    resetError={resetError}
                                />
                            )}>
                            <RodinGenerateBoard/>
                        </Sentry.ErrorBoundary>
                    </>
                )}
            </div>
            {/*{isMobile && (*/}
            {/*    <button className={`${style.tipsForViewSwitch}`} style={{top: isSafari ? '84vh' : '88vh'}} onClick={isAtTop ? scrollToBottom : scrollToTop}>*/}
            {/*        {isAtTop ? (*/}
            {/*            <><FiChevronsDown style={{marginRight: '10px'}}/> Scroll to editor</>*/}
            {/*        ) : (*/}
            {/*            <><FiChevronsUp style={{marginRight: '10px'}}/> Scroll to preview</>*/}
            {/*        )}*/}
            {/*    </button>*/}
            {/*)}*/}
            {/* {modalObj.type === 'generate' && !generate && <Recommend type={'prompt'} openStatus={screen.width > 1280} />} */}
        </div>
    )
}

export {Modal};
